import { AppSettings, defaultSettings } from './types';
import { normalizeDudaIntegration } from './dudaAccounts';
import { normalizeMetaIntegration } from './metaAccounts';
import { defaultStripeBilling } from './types';

const STORAGE_KEY = 'saas_hub_settings_v2';
const LEGACY_KEY = 'saas_hub_settings_v1';

function env(k: string | undefined): string {
  return (k && k.trim()) || '';
}

function applyEnvFallbacks(settings: AppSettings): AppSettings {
  const dudaAccounts = settings.integrations.duda.accounts.map((a, i) => {
    if (i === 0) {
      return {
        ...a,
        apiUser:
          a.apiUser?.trim() ||
          env(process.env.REACT_APP_DUDA_API_USER) ||
          env(process.env.REACT_APP_DUDA_USER) ||
          env(process.env.REACT_APP_DUDA_USERNAME),
        apiPassword:
          a.apiPassword?.trim() ||
          env(process.env.REACT_APP_DUDA_API_PASSWORD) ||
          env(process.env.REACT_APP_DUDA_PASSWORD) ||
          env(process.env.REACT_APP_DUDA_API_SECRET),
        siteName:
          a.siteName?.trim() ||
          env(process.env.REACT_APP_DUDA_SITE_NAME) ||
          env(process.env.REACT_APP_DUDA_SITE_ID),
      };
    }
    if (i === 1) {
      return {
        ...a,
        apiUser: a.apiUser?.trim() || env(process.env.REACT_APP_DUDA2_API_USER),
        apiPassword: a.apiPassword?.trim() || env(process.env.REACT_APP_DUDA2_API_PASSWORD),
        siteName: a.siteName?.trim() || env(process.env.REACT_APP_DUDA2_SITE_NAME),
      };
    }
    return a;
  });

  return {
    ...settings,
    integrations: {
      ...settings.integrations,
      stripe: {
        publishableKey:
          settings.integrations.stripe.publishableKey?.trim() ||
          env(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY),
        secretKey:
          settings.integrations.stripe.secretKey?.trim() ||
          env(process.env.REACT_APP_STRIPE_SECRET_KEY),
        billing: {
          ...defaultStripeBilling(),
          ...settings.integrations.stripe.billing,
        },
      },
      xero: {
        clientId:
          settings.integrations.xero.clientId?.trim() || env(process.env.REACT_APP_XERO_CLIENT_ID),
        clientSecret:
          settings.integrations.xero.clientSecret?.trim() ||
          env(process.env.REACT_APP_XERO_CLIENT_SECRET),
        redirectUri:
          settings.integrations.xero.redirectUri?.trim() ||
          env(process.env.REACT_APP_XERO_REDIRECT_URI),
        collections: {
          ...defaultSettings().integrations.xero.collections,
          ...settings.integrations.xero.collections,
        },
      },
      duda: { ...settings.integrations.duda, accounts: dudaAccounts },
      googleAds: {
        ...settings.integrations.googleAds,
        developerToken:
          settings.integrations.googleAds.developerToken?.trim() ||
          env(process.env.REACT_APP_GOOGLE_ADS_DEVELOPER_TOKEN),
        clientId:
          settings.integrations.googleAds.clientId?.trim() ||
          env(process.env.REACT_APP_GOOGLE_ADS_CLIENT_ID),
        clientSecret:
          settings.integrations.googleAds.clientSecret?.trim() ||
          env(process.env.REACT_APP_GOOGLE_ADS_CLIENT_SECRET),
        customerId:
          settings.integrations.googleAds.customerId?.trim() ||
          env(process.env.REACT_APP_GOOGLE_ADS_CUSTOMER_ID),
        refreshToken:
          settings.integrations.googleAds.refreshToken?.trim() ||
          env(process.env.REACT_APP_GOOGLE_ADS_REFRESH_TOKEN),
      },
    },
  };
}

function migrateV1(parsed: Record<string, unknown>): AppSettings {
  const base = defaultSettings();
  const i = (parsed.integrations || {}) as Record<string, unknown>;
  return {
    ...base,
    workspace: { ...base.workspace, ...(parsed.workspace as object) },
    integrations: {
      ...base.integrations,
      stripe: { ...base.integrations.stripe, ...(i.stripe as object) },
      xero: { ...base.integrations.xero, ...(i.xero as object) },
      duda: normalizeDudaIntegration(i.duda, base.integrations.duda),
      googleAds: { ...base.integrations.googleAds, ...(i.googleAds as object) },
    },
  };
}

function mergeStored(): AppSettings {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(LEGACY_KEY);
    }
    if (!raw) {
      return defaultSettings();
    }
    const parsed = JSON.parse(raw) as { version?: number; integrations?: unknown; workspace?: unknown };
    if (parsed.version === 2 && parsed.integrations) {
      const base = defaultSettings();
      const p = parsed as Partial<AppSettings>;
      return {
        ...base,
        ...p,
        workspace: { ...base.workspace, ...(p.workspace as object) },
        integrations: {
          ...base.integrations,
          ...p.integrations!,
          stripe: {
            ...base.integrations.stripe,
            ...p.integrations!.stripe,
            billing: {
              ...defaultStripeBilling(),
              ...p.integrations!.stripe?.billing,
            },
          },
          duda: normalizeDudaIntegration(p.integrations!.duda, base.integrations.duda),
          meta: normalizeMetaIntegration(p.integrations!.meta, base.integrations.meta),
        },
      };
    }
    if (parsed.version === 1 && parsed.integrations) {
      return migrateV1(parsed as Record<string, unknown>);
    }
    return defaultSettings();
  } catch {
    return defaultSettings();
  }
}

export function loadSettings(): AppSettings {
  return applyEnvFallbacks(mergeStored());
}

export function saveSettings(settings: AppSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function subscribeSettings(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === LEGACY_KEY) {
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
